'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { login, logout, register } from '@/services/authService'
import { LoginRequest } from '@/utils/schemas'
import { LoginResponse } from '@/models/auth.model'
import { useAuthStore } from '@/stores/authStores'
import { useEffect, useState } from 'react'

export const useAuth = () => {
  const queryClient = useQueryClient()
  const { setUserInfo, clearUserInfo, userInfo, isLoggedIn } = useAuthStore()

  const [isHydrated, setIsHydrated] = useState(false) // Đánh dấu trạng thái đã hydrate

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('auth')
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser)?.state?.userInfo
        if (parsedUser) {
          setUserInfo(parsedUser)
        }
      }
      setIsHydrated(true) // Đánh dấu đã tải xong dữ liệu từ localStorage
    }
  }, [setUserInfo])

  const loginMutation = useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: login,
    onSuccess: ({ userInfo }) => {
      setUserInfo(userInfo)
      queryClient.setQueryData(['userInfo'], userInfo)
      toast.success('Đăng nhập thành công!')
    },
    onError: () => {
      toast.error('Đăng nhập thất bại!')
    },
  })

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearUserInfo()
      queryClient.removeQueries({ queryKey: ['userInfo'] })
      toast.success('Đăng xuất thành công!')
    },
    onError: () => {
      toast.error('Đăng xuất thất bại!')
    },
  })

  const registerMutation = useMutation({
    mutationFn: register,
    onSuccess: () => {
      toast.success('Đăng ký thành công!')
    },
    onError: () => {
      toast.error('Đăng ký thất bại!')
    },
  })

  return {
    signin: loginMutation.mutateAsync,
    signout: logoutMutation.mutateAsync,
    signup: registerMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggedIn: isHydrated ? isLoggedIn : false, // Chỉ trả về trạng thái khi đã hydrate
    userInfo: isHydrated ? userInfo : null, // Trả về null nếu chưa hydrate
    isHydrated, // Thêm trạng thái hydrate để kiểm tra bên ngoài
  }
}
